import React from 'react';
import {
  IconMicrophone,
  IconMessageCircle,
  IconPlug,
  IconRobot,
  IconBrain,
  IconWriting,
  IconHeadset,
  IconTransform,
  IconApi,
  IconDatabase,
  IconPhone,
  IconCalendar,
  IconMail,
  IconChartBar,
  IconSearch,
  IconShield,
  IconFileText,
} from '@tabler/icons-react';

/**
 * AgentAvatar — scalable agent identity component.
 *
 * Three-layer fallback:
 *   1. Custom uploaded icon (SVG/PNG via iconUrl)
 *   2. Tabler glyph icon (mapped from agentType/category)
 *   3. Generated initials (from name)
 *
 * Props:
 *  - name: string
 *  - size: 'xs' | 'sm' | 'md' | 'lg'
 *  - agentType: string (voice, text, webhook, operator, research, marketing, support, automation, integration, data)
 *  - iconUrl: string | null (custom uploaded icon URL — highest priority)
 *  - className: string
 */

// ── Agent type → Tabler icon mapping ──
const AGENT_ICON_MAP = {
  voice: IconMicrophone,
  voice_agent: IconMicrophone,
  live_voice_s2s_agent: IconMicrophone,
  voice_input_agent: IconMicrophone,
  voice_output_tts_agent: IconMicrophone,
  text: IconMessageCircle,
  text_agent: IconMessageCircle,
  webhook: IconPlug,
  webhook_agent: IconPlug,
  operator: IconRobot,
  research: IconBrain,
  research_agent: IconBrain,
  marketing: IconWriting,
  marketing_agent: IconWriting,
  support: IconHeadset,
  support_agent: IconHeadset,
  automation: IconTransform,
  automation_agent: IconTransform,
  integration: IconApi,
  integration_agent: IconApi,
  data: IconDatabase,
  data_agent: IconDatabase,
  phone: IconPhone,
  calendar: IconCalendar,
  email: IconMail,
  analytics: IconChartBar,
  search: IconSearch,
  security: IconShield,
  document: IconFileText,
};

// ── Color palette (stable hash from name) ──
const PALETTE = [
  { bg: 'bg-indigo-500/12', text: 'text-indigo-400', border: 'border-indigo-500/15' },
  { bg: 'bg-violet-500/12', text: 'text-violet-400', border: 'border-violet-500/15' },
  { bg: 'bg-blue-500/12', text: 'text-blue-400', border: 'border-blue-500/15' },
  { bg: 'bg-cyan-500/12', text: 'text-cyan-400', border: 'border-cyan-500/15' },
  { bg: 'bg-teal-500/12', text: 'text-teal-400', border: 'border-teal-500/15' },
  { bg: 'bg-emerald-500/12', text: 'text-emerald-400', border: 'border-emerald-500/15' },
  { bg: 'bg-amber-500/12', text: 'text-amber-400', border: 'border-amber-500/15' },
  { bg: 'bg-rose-500/12', text: 'text-rose-400', border: 'border-rose-500/15' },
  { bg: 'bg-pink-500/12', text: 'text-pink-400', border: 'border-pink-500/15' },
  { bg: 'bg-fuchsia-500/12', text: 'text-fuchsia-400', border: 'border-fuchsia-500/15' },
];

const SIZE_MAP = {
  xs: { container: 'w-7 h-7', icon: 14, text: 'text-[10px]', radius: 'rounded-md' },
  sm: { container: 'w-9 h-9', icon: 18, text: 'text-xs', radius: 'rounded-lg' },
  md: { container: 'w-11 h-11', icon: 22, text: 'text-sm', radius: 'rounded-lg' },
  lg: { container: 'w-14 h-14', icon: 28, text: 'text-base', radius: 'rounded-xl' },
};

function getInitials(name) {
  if (!name) return '?';
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

function hashName(name) {
  if (!name) return 0;
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash);
}

/**
 * Infer agent type from agent data.
 * Checks: agentType prop → capability_profile → category → name keywords
 */
function inferAgentType(agentType, agent) {
  if (agentType && AGENT_ICON_MAP[agentType]) return agentType;

  // Check capability_profile - handle both string and object formats
  const rawProfile = agent?.capability_profile;
  const profile = typeof rawProfile === 'string' ? rawProfile : '';
  if (profile.includes('voice') || profile.includes('s2s')) return 'voice';
  if (profile.includes('webhook')) return 'webhook';

  // Check category
  const cat = (agent?.category || '').toLowerCase();
  if (cat.includes('research') || cat.includes('analys')) return 'research';
  if (cat.includes('marketing') || cat.includes('content')) return 'marketing';
  if (cat.includes('support') || cat.includes('customer')) return 'support';
  if (cat.includes('integrat')) return 'integration';
  if (cat.includes('data') || cat.includes('pipeline')) return 'data';
  if (cat.includes('automat') || cat.includes('workflow')) return 'automation';
  if (cat.includes('secur') || cat.includes('complian')) return 'security';

  // Check name keywords
  const nameLower = (agent?.name || '').toLowerCase();
  if (nameLower.includes('voice') || nameLower.includes('call') || nameLower.includes('phone')) return 'voice';
  if (nameLower.includes('operator')) return 'operator';
  if (nameLower.includes('research') || nameLower.includes('analys')) return 'research';
  if (nameLower.includes('market') || nameLower.includes('content') || nameLower.includes('seo')) return 'marketing';
  if (nameLower.includes('support') || nameLower.includes('helpdesk') || nameLower.includes('ticket')) return 'support';
  if (nameLower.includes('webhook') || nameLower.includes('trigger')) return 'webhook';
  if (nameLower.includes('integrat') || nameLower.includes('connect')) return 'integration';
  if (nameLower.includes('data') || nameLower.includes('pipeline') || nameLower.includes('etl')) return 'data';
  if (nameLower.includes('automat') || nameLower.includes('workflow')) return 'automation';
  if (nameLower.includes('email') || nameLower.includes('mail')) return 'email';
  if (nameLower.includes('booking') || nameLower.includes('schedule') || nameLower.includes('calendar')) return 'calendar';
  if (nameLower.includes('document') || nameLower.includes('report')) return 'document';
  if (nameLower.includes('search') || nameLower.includes('find')) return 'search';

  return null; // Falls back to initials
}

export const AgentAvatar = ({
  name = '',
  size = 'sm',
  agentType = null,
  iconUrl = null,
  agent = null,
  className = '',
}) => {
  const s = SIZE_MAP[size] || SIZE_MAP.sm;
  const color = PALETTE[hashName(name) % PALETTE.length];
  const resolvedType = inferAgentType(agentType, agent || { name });

  // Layer 0: Operators ALWAYS use Orion robot avatar (brand rule)
  const isOperator = agent?.isOperator || resolvedType === 'operator';
  if (isOperator) {
    return (
      <div
        className={`${s.container} ${s.radius} overflow-hidden border border-[var(--border-default)] shrink-0 bg-[#0D0E12] ${className}`}
        data-testid="agent-avatar"
        title={name}
      >
        <img
          src={`${process.env.PUBLIC_URL}/orion-icon-192.png`}
          alt={name}
          className="w-full h-full object-cover"
        />
      </div>
    );
  }

  // Layer 1: Custom uploaded icon (marketplace or user-uploaded)
  if (iconUrl) {
    return (
      <div
        className={`${s.container} ${s.radius} overflow-hidden border border-[var(--border-default)] shrink-0 bg-[var(--surface-3)] ${className}`}
        data-testid="agent-avatar"
        title={name}
      >
        <img
          src={iconUrl}
          alt={name}
          className="w-full h-full object-cover"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      </div>
    );
  }

  // Layer 2: Tabler glyph icon (inferred from agent type/category/name)
  const TablerIcon = resolvedType ? AGENT_ICON_MAP[resolvedType] : null;

  if (TablerIcon) {
    return (
      <div
        className={`${s.container} ${s.radius} ${color.bg} border ${color.border} flex items-center justify-center shrink-0 ${className}`}
        data-testid="agent-avatar"
        title={name}
      >
        <TablerIcon size={s.icon} className={color.text} stroke={1.5} />
      </div>
    );
  }

  // Layer 3: Generated initials fallback
  const initials = getInitials(name);
  return (
    <div
      className={`${s.container} ${s.radius} ${color.bg} border ${color.border} flex items-center justify-center shrink-0 ${className}`}
      data-testid="agent-avatar"
      title={name}
    >
      <span className={`${s.text} ${color.text} font-semibold leading-none select-none`}>
        {initials}
      </span>
    </div>
  );
};

export default AgentAvatar;
